import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { useCreditGoal } from '@/hooks/queries/useGoals';
import { SECTION_ID } from '@/components/analytics/CreditByMonth';
import { formatCurrency } from '@/lib/formatters';
import type { CreditMonth } from '@/lib/api/client';

export interface CreditGoalTileProps {
  /** The month in progress. Null only when the read failed — the API always includes it. */
  current: CreditMonth | null;
  /**
   * The STANDING goal. Required, and genuinely used: compared against `current.goal` it is what
   * tells an override-only month apart from an ordinary one.
   */
  goal: number | null;
}

/** Whole dollars here; the analytics table carries the cents. */
const dollars = (value: number) => formatCurrency(value, { cents: false });

export function CreditGoalTileView({ current, goal }: CreditGoalTileProps) {
  if (!current) return null;

  const target = current.goal;
  const isOverride = target !== null && target !== goal;
  const percent = target === null || target === 0
    ? null
    // On NET, and clamped at both ends: a losing month is an empty bar, not a negative width.
    : Math.max(0, Math.min(100, Math.round((current.net / target) * 100)));

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Net credit this month</CardTitle>
        {/* A header link, not a whole-card one: the body already holds a link to Settings. */}
        <Link
          to={`/analytics#${SECTION_ID}`}
          data-testid="credit-by-month-link"
          className="text-sm text-brand-600 hover:text-brand-700 whitespace-nowrap"
        >
          By month
        </Link>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-baseline justify-between">
          {/* The total opens what it is made of: this month's line items on analytics. */}
          <Link
            to={`/analytics?month=${current.month}#${SECTION_ID}`}
            data-testid="credit-net"
            aria-label={`${dollars(current.net)} net credit this month — see what made it up`}
            className="text-2xl font-semibold text-gray-900 hover:text-brand-700 hover:underline"
          >
            {dollars(current.net)}
          </Link>
          {target !== null && (
            <span data-testid="credit-goal-target" className="text-sm text-gray-500">
              of {dollars(target)}{isOverride ? ' (this month only)' : ''}
            </span>
          )}
        </div>

        {percent !== null && (
          <div className="h-2 w-full rounded-full bg-gray-100">
            <div
              role="progressbar"
              aria-label="Net credit against the monthly goal"
              aria-valuenow={percent}
              aria-valuemin={0}
              aria-valuemax={100}
              className={`h-2 rounded-full ${current.hit ? 'bg-green-500' : 'bg-brand-600'}`}
              style={{ width: `${percent}%` }}
            />
          </div>
        )}

        {target === null && (
          <p data-testid="credit-goal-empty" className="text-sm text-gray-500">
            No goal set — <Link to="/settings" className="text-brand-600 hover:underline">set one in Settings</Link>
          </p>
        )}

        {target !== null && current.hit && (
          <p data-testid="credit-goal-hit" className="text-sm font-medium text-green-700">
            Goal met &middot; {dollars(current.over ?? 0)} over
          </p>
        )}

        {target !== null && !current.hit && (
          <p data-testid="credit-goal-progress" className="text-sm text-gray-500">
            {dollars(current.remaining ?? 0)} remaining &middot;{' '}
            {current.daysLeft === 0
              ? 'no trading days left'
              : `${current.daysLeft} trading days left · ${dollars(current.paceNeeded ?? 0)} a day`}
          </p>
        )}

        {/* Gross stays on the tile: it is what was SOLD, and the gap between the two is the cost
            of closing and rolling — the thing the net figure alone would hide. */}
        <p data-testid="credit-goal-gross" className="text-sm text-gray-500 border-t border-gray-100 pt-3">
          Credit sold <span className="font-semibold text-gray-900">{dollars(current.gross)}</span>
          {' '}&middot; costs <span className="font-semibold text-gray-900">{dollars(current.costs)}</span>
        </p>
      </CardContent>
    </Card>
  );
}

export function CreditGoalTile() {
  // One month: the tile only ever shows the month in progress, which the API always includes.
  const { data, isLoading, isError } = useCreditGoal(1);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Net credit this month</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <Spinner />
        </CardContent>
      </Card>
    );
  }

  if (isError || !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Net credit this month</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500">Could not load</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <CreditGoalTileView
      current={data.months.find((m) => m.isCurrent) ?? null}
      goal={data.goal}
    />
  );
}
